import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Home, 
  PiggyBank, 
  HandHeart, 
  TrendingUp, 
  Users, 
  MapPin 
} from "lucide-react";
import janineBorisHardi from "@/assets/janine-boris-hardi.jpg";
import janineBorisHardiWebP from "@/assets/janine-boris-hardi.webp";

const rentingOptions = [
  {
    id: "leibrente",
    title: "Leibrente",
    icon: PiggyBank,
    description: "Monatliche Rente bis zum Lebensende bei Verbleib in der Immobilie",
    features: ["Lebenslange Zahlung", "Wohnrecht bleibt", "Keine Erbschaftssteuer"],
    idealFor: "Senioren ab 75 Jahren, die Sicherheit schätzen"
  },
  {
    id: "teilverkauf",
    title: "Teilverkauf",
    icon: TrendingUp,
    description: "Verkauf eines Teils der Immobilie bei Erhalt des Wohnrechts",
    features: ["Sofortige Auszahlung", "Flexibel wählbarer Anteil", "Erbe bleibt teilweise erhalten"],
    idealFor: "Eigentümer, die Flexibilität und Liquidität benötigen"
  },
  {
    id: 3,
    title: "Rückmiete",
    icon: Home,
    description: "Verkauf der Immobilie mit anschließender Anmietung",
    features: ["Komplette Liquidität", "Planbare Mietkosten", "Keine Instandhaltung"],
    idealFor: "Bei hohem Kapitalbedarf oder Umzugswunsch"
  },
  {
    id: 4,
    title: "Nießbrauch",
    icon: HandHeart,
    description: "Übertragung an Erben mit lebenslangem Wohn- und Nutznießrecht",
    features: ["Schenkung zu Lebzeiten", "Steuervorteile", "Erbschaft geregelt"],
    idealFor: "Familienorientierte Lösung mit Steueroptimierung"
  },
  {
    id: 5,
    title: "Wohnrecht",
    icon: Users,
    description: "Wohnen bleiben mit Grundbuchlich gesichertem Wohnrecht",
    features: ["Einmalzahlung oder Zeitrente", "Sicherheit", "Eigener Herr sein"],
    idealFor: "Eigentümer die mit Kapitalbedarf und Sicherheit"
  },
  {
    id: 6,
    title: "Seniorenfinanzierung",
    icon: MapPin,
    description: "Kredit gegen Immobilie als Sicherheit ohne monatliche Raten",
    features: ["Flexible Auszahlung", "Keine Tilgung zu Lebzeiten", "Rückzahlung bei Tod oder Übernahme durch Erben"],
    idealFor: "Temporären Liquiditätsbedarf mit Eigentumserhalt"
  }
];

const RentingOptions = () => {
  return (
    <section className="py-16 md:py-24">
      <div className="container-comfortable">
        {/* Team Image Section */}
        <div className="text-center mb-12">
          <picture>
            <source 
              srcSet={janineBorisHardiWebP} 
              type="image/webp"
              sizes="(max-width: 640px) 280px, (max-width: 768px) 320px, 320px"
            />
            <img 
              src={janineBorisHardi} 
              alt="Janine und Boris Hardi - Ihre Immobilienverrentungs-Experten"
              className="w-80 h-60 object-cover rounded-xl mx-auto mb-4 shadow-lg"
              fetchPriority="high"
              width="320"
              height="240"
              sizes="(max-width: 640px) 280px, (max-width: 768px) 320px, 320px"
            />
          </picture>
          <p className="text-lg text-muted-foreground font-medium">
            Ein kostenloser Service von RentePlusImmobilie - Ihre Janine und Boris Hardi
          </p>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            6 bewährte Wege der Immobilienverrentung
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-balance">
            Jede Situation ist einzigartig. Finden Sie die passende Lösung 
            für Ihre persönlichen Bedürfnisse und Ziele.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {rentingOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <Card key={option.id} className="card-elevated h-full hover:shadow-card transition-all duration-300">
                <CardHeader className="text-center pb-4">
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-xl md:text-2xl text-primary">
                    {option.title}
                  </CardTitle>
                  <CardDescription className="text-lg leading-relaxed">
                    {option.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-primary text-lg">Vorteile:</h4>
                    <ul className="space-y-1">
                      {option.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-muted-foreground">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-2 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-4">
                      <strong>Ideal für:</strong> {option.idealFor}
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        if (option.id === "leibrente") {
                          window.location.href = "/leibrente";
                        } else if (option.id === "teilverkauf") {
                          window.location.href = "/teilverkauf";
                        } else if (option.id === 3) {
                          window.location.href = "/rueckmiete";
                        } else if (option.id === 4) {
                          window.location.href = "/niessbrauch";
                        } else if (option.id === 5) {
                          window.location.href = "/wohnrecht";
                        } else if (option.id === 6) {
                          window.location.href = "/seniorenfinanzierung";
                        }
                      }}
                    >
                      Mehr erfahren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button 
            variant="accent" 
            size="lg" 
            className="text-lg md:text-xl px-6 md:px-12 w-full max-w-md mx-auto"
            onClick={() => {
              const advisorSection = document.querySelector('[data-advisor-wizard]');
              if (advisorSection) {
                advisorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            Welche Option passt zu mir?
          </Button>
        </div>
      </div>
    </section>
  );
};

export default RentingOptions;